import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Form, Input, Select, Button, Typography, Space, Spin } from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNotify } from "../../../context/NotificationContext";
import { useCommon } from "../../../context/CommonContext";
import announcementService from "../../../services/announcementService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";

const { Title } = Typography;

const AddAnnouncement = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPageTitle("Add Announcement");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const payload = {
        // The API falls back to the title when no internal name is given, so
        // an empty field is sent as undefined rather than "".
        announcement_name: values.announcement_name?.trim() || undefined,
        announcement_title: values.announcement_title?.trim(),
        announcement_subtitle: values.announcement_subtitle?.trim() || null,
        announcement_is_active: values.announcement_is_active ?? "Y",
      };

      const response = await announcementService.createAnnouncement(payload);

      if (response?.data?.status) {
        notify.success({
          message: response.data.message || "Announcement created successfully",
        });
        navigate("/announcements");
      } else {
        notify.error({
          message: response?.data?.message || "Failed to create announcement",
        });
      }
    } catch (error) {
      console.error("Error creating announcement:", error);
      notify.error({
        message: error?.data?.message || "Error creating announcement",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={saving}>
      <Card>
        <Title level={4} style={{ marginTop: 0, marginBottom: 24 }}>
          Add Announcement
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ announcement_is_active: "Y" }}
          style={{ maxWidth: 720 }}
        >
          <Form.Item
            name="announcement_title"
            label="Title"
            rules={[
              { required: true, message: "Please enter a title" },
              { validator: noWhitespaceValidator },
            ]}
          >
            <Input placeholder="e.g. Midnight Premiere" size="large" allowClear />
          </Form.Item>

          {/* The card's whole second line, typed as it should read — separator
              included. There is no date field: "Shadow Who · 12 Jul" and
              "Till 30 Jun 2026" are both just this string. */}
          <Form.Item name="announcement_subtitle" label="Subtitle">
            <Input
              placeholder="e.g. Shadow Who · 12 Jul"
              size="large"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="announcement_name"
            label="Internal Name"
            rules={[
              { required: true, message: "Please enter an internal name" },
              { validator: noWhitespaceValidator },
            ]}
            extra="Only shown in the CMS, never to viewers."
          >
            <Input placeholder="e.g. July premiere card" size="large" allowClear />
          </Form.Item>

          <Form.Item name="announcement_is_active" label="Status">
            <Select
              size="large"
              options={[
                { label: "Active", value: "Y" },
                { label: "Inactive", value: "N" },
              ]}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Save
              </Button>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/announcements")}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Spin>
  );
};

export default AddAnnouncement;
